import { CheckCircleOutlined } from '@ant-design/icons';
import { Alert, Form, Input } from 'antd';
import { useContext, useMemo } from 'react';

import { SCard } from 'app/components/StaycationUI';
import { FormLineLayout } from 'app/components/commons/Form/FormLineLayout/FormLineLayout';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { useHotels } from 'app/hooks/data/hotels/useHotel';
import type { OpeningHours } from 'app/typings/core/admin/packs/openingHours/openingHours';
import type { PackResponse } from 'app/typings/core/admin/packs/pack';
import { sanitizeRateCode } from 'app/utils/strings';

import '../General.scss';

const RULES = [{ required: true, message: 'Required' }];

export const Naming = () => {
  const { isEditMode } = useContext(ViewEditModeContext);
  const { data: hotels } = useHotels();

  const form = Form.useFormInstance<
    Partial<PackResponse> & {
      isDaytime: boolean;
      aliasForUser: string;
      openingHours: OpeningHours;
    }
  >();
  const formValues = Form.useWatch([], form);
  const showUserAlert = formValues?.isDaytime;

  const selectedHotel = useMemo(
    () => hotels?.find((hotel) => hotel.id === formValues?.hotelId),
    [hotels, formValues?.hotelId]
  );

  return (
    <SCard title="Naming" className="pack-form">
      <FormLineLayout label="Name for hotel" labelOnTop isRequired>
        <div className="naming-field-wrapper">
          <Form.Item name="nameForHotel" rules={RULES}>
            <Input placeholder="Enter a hotel name" disabled={!isEditMode} />
          </Form.Item>
          <div className="helper-text">
            Shown on the Extranet and Bills (E.g. Spa package, Dinner package)
          </div>
        </div>
      </FormLineLayout>
      {selectedHotel?.channelManager && !formValues?.isDaytime && (
        <FormLineLayout label="Rate code for hotel" labelOnTop>
          <div className="naming-field-wrapper">
            <Form.Item
              name="channelManagerRateCode"
              normalize={sanitizeRateCode}
            >
              <Input disabled={!isEditMode} />
            </Form.Item>
            <div className="helper-text">
              Code generated by the hotel and used to connect the package to its
              channel manager.
            </div>
          </div>
        </FormLineLayout>
      )}
      <FormLineLayout label="Name for user" labelOnTop isRequired>
        <div className="naming-field-wrapper">
          <Form.Item name="nameForUser" rules={RULES}>
            <Input placeholder="Enter a user name" disabled={!isEditMode} />
          </Form.Item>

          {formValues?.isDaytime && (
            <div className="helper-text">
              Shown on daytime cards and package page
            </div>
          )}
          {!formValues?.isDaytime && (
            <div className="helper-text">
              Shown on Offer page and Search results. <br />
              If this is the hotel's only package, leave empty.
            </div>
          )}
          {isEditMode && showUserAlert && (
            <Alert
              type="info"
              message={
                <div className="alert__title">Name for user guidelines</div>
              }
              description={
                <div className="alert__description">
                  List the main experiences and capitalize them - e.g., Massage
                  1h, Spa et Cocktail Be descriptive and factual
                  <br />
                  <br />
                  Include duration for massages and spa access (if the package
                  includes both, only mention the massage duration)
                  <br />
                  <br />
                  Add factual details only if relevant:
                  <br />
                  <span className="emoji">✅</span> Do: Dîner bistronomique /
                  Cocktail ambiance Art Déco / Petit déjeuner buffet / Tea time
                  signé Nina Métayer
                  <br />
                  <span className="emoji">❌</span> Don’t: Brunch iconique /
                  Petit déjeuner à la Défense / Dîner moderne et créatif
                  <br />
                  <br />
                  <span className="emoji">⚠️</span> Review titles by category
                  (spa, massage, dinner, etc.) to ensure they are not too
                  repetitive and remain descriptive enough to help users compare
                  packs.
                </div>
              }
              icon={<CheckCircleOutlined />}
              showIcon
            />
          )}
        </div>
      </FormLineLayout>
    </SCard>
  );
};
